import {
  Group,
  Flex,
  Button,
  useMantineTheme,
  Text,
  ActionIcon,
  CloseButton,
  Transition,
} from "@mantine/core";
import { useContext, useEffect, useState } from "react";
import { MdClose, MdPlaylistAdd } from "react-icons/md";
import DatePicker from "src/components/DatePicker";
import ScheduledCharge, { IFormProps } from "./ScheduleCharge";
import { AppContext } from "src/api/AppContext";
import {
  useCreateScheduledTransactionMutation,
  useUpdateScheduledTransactionMutation,
  ScheduledTransactionStatus,
  useDeleteScheduledChargesMutation,
  useScheduledChargesByTransactionIdLazyQuery,
  ScheduledChargesByTransactionIdQuery,
} from "src/generated/graphql";
import { useClickOutside } from "@mantine/hooks";
import { CRUDSTATES } from "src/utils/constants";
import { IconPencil } from "@tabler/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import { IoMdSave } from "react-icons/io";
import { BiBlock } from "react-icons/bi";

interface IProps {
  state: CRUDSTATES;
  clientId: string;
  subscriptionId?: string;
  scheduledTransactionId?: string;
  transactionDate?: Date;
  onRefetch?: () => void;
  closeModal: () => void;
}

interface ITaxCharge {
  id: string;
  organizationTaxRate: {
    id: string;
    name: string;
  };
}
export interface ICharge {
  id: string;
  name: string;
  cost: number;
  isPostTax: boolean;
  isTaxable: boolean;
  revenueCategory: {
    id: string;
    name: string;
  };
  taxRates?: ITaxCharge[] | null;
}

const SchedulePayment = ({
  state,
  clientId,
  subscriptionId,
  scheduledTransactionId,
  transactionDate,
  onRefetch,
  closeModal,
}: IProps) => {
  const theme = useMantineTheme();
  const { currentLocation } = useContext(AppContext);

  const nextWeekDate = new Date().setDate(new Date().getDate() + 1);
  const scheduleDate = transactionDate
    ? new Date(transactionDate)
    : new Date(nextWeekDate);
  const [modalState, setModalState] = useState(state);
  const [scheduledDate, setScheduleDate] = useState(scheduleDate);
  const [isCharge, setIsCharge] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState<IFormProps>();
  const [isDateDisabled, setIsDateDisabled] = useState(
    modalState === CRUDSTATES.Update
  );
  const [scheduleTransactionId, setScheduleTransactionId] = useState(
    scheduledTransactionId
  );
  const [activeOption, setActiveOption] = useState<
    { name: string; id: string } | undefined
  >(undefined);
  const outsideClickRef = useClickOutside(() => {
    setActiveOption(undefined);
  });

  const [createScheduleTransaction, { loading: createLoading }] =
    useCreateScheduledTransactionMutation({
      onCompleted: (res) => {
        setScheduleTransactionId(res.createScheduledTransaction.id);
        onRefetch && onRefetch();
        setModalState(CRUDSTATES.Update);
        setIsDateDisabled(true);
      },
    });

  const [updateScheduleTransaction, { loading: updateLoading }] =
    useUpdateScheduledTransactionMutation({
      onCompleted: (res) => {
        setIsDateDisabled(true);
        onRefetch && onRefetch();
      },
    });

  const [
    getTransactionCharges,
    { data: scheduledCharges, loading: chargesLoading },
  ] = useScheduledChargesByTransactionIdLazyQuery({
    onCompleted: () => {},
  });

  const onRefetchCharges = () => {
    if (scheduleTransactionId) {
      getTransactionCharges({
        variables: {
          id: scheduleTransactionId,
        },
        fetchPolicy: "network-only",
      });
    }
    onRefetch && onRefetch();
  };

  const [deleteScheduleCharge, { loading: deleteLoading }] =
    useDeleteScheduledChargesMutation({
      onCompleted: (res) => {
        setIsDateDisabled(true);
        onRefetchCharges();
        onRefetch && onRefetch();
      },
    });

  useEffect(() => {
    onRefetchCharges();
  }, [scheduleTransactionId]);

  const handleCreateSchedulePayment = () => {
    if (modalState === CRUDSTATES.Create) {
      createScheduleTransaction({
        variables: {
          data: {
            ...(subscriptionId ? { subscription: { id: subscriptionId } } : {}),
            client: { id: clientId },
            location: { id: currentLocation?.id! },
            status: ScheduledTransactionStatus.New,
            processAt: scheduledDate,
          },
        },
      });
    } else {
      updateScheduleTransaction({
        variables: {
          id: scheduleTransactionId!,
          data: {
            processAt: scheduledDate,
          },
        },
      });
    }
  };

  const handleOpenListMenu = (option: { name: string; id: string }) => {
    setActiveOption(option);
  };

  const handleEditScheduleCharge = (
    charge: ScheduledChargesByTransactionIdQuery["scheduledChargesByTransactionId"]["0"]
  ) => {
    const { revenueCategory, taxRates, ...rest } = charge;
    const chargeData: IFormProps = {
      categoryOption: revenueCategory.id,
      taxRates: taxRates?.map((e) => e.id),
      ...rest,
    };

    setSelectedCharge(chargeData);
    setIsCharge(true);
  };

  const handleDeleteScheduleCharge = (chargeId: string) => {
    deleteScheduleCharge({
      variables: {
        id: chargeId,
      },
    });
  };

  const totalCostCharges =
    scheduledCharges?.scheduledChargesByTransactionId.reduce(
      (partialSum, charge) => partialSum + charge.cost,
      0
    );

  return (
    <>
      {!isCharge ? (
        <>
          <Flex justify={"space-between"} mb={15}>
            <Text size={20} fw={500}>
              {modalState} Scheduled Transaction
            </Text>
            <ActionIcon size="lg" onClick={closeModal}>
              <MdClose size={20} />
            </ActionIcon>
          </Flex>
          {modalState === CRUDSTATES.Create ? (
            <DatePicker
              label="Start Date"
              mx="auto"
              value={scheduledDate}
              onChange={(e: Date) => {
                setScheduleDate(e);
              }}
              minDate={new Date(nextWeekDate)}
            />
          ) : (
            <Flex align={"center"} justify={"space-around"}>
              <DatePicker
                label="Start Date"
                mx="auto"
                value={scheduledDate}
                onChange={(e: Date) => {
                  setScheduleDate(e);
                }}
                disabled={isDateDisabled}
                w={"90%"}
                minDate={new Date(nextWeekDate)}
              />
              {isDateDisabled ? (
                <IconPencil
                  color={theme.colors.secondaryTextColor[6]}
                  size={25}
                  onClick={() => {
                    setIsDateDisabled(false);
                  }}
                />
              ) : (
                <IoMdSave
                  color={theme.colors.secondaryTextColor[6]}
                  size={25}
                  onClick={() => {
                    handleCreateSchedulePayment();
                  }}
                />
              )}
            </Flex>
          )}

          {modalState === CRUDSTATES.Update ? (
            <>
              <Flex direction={"column"}>
                <Flex justify={"space-between"} my={20}>
                  <Text fw={500}>Charges</Text>
                  <Flex align={"center"}>
                    <Text>$ {totalCostCharges}</Text>
                    <ActionIcon
                      ml={4}
                      size="sm"
                      variant="transparent"
                      onClick={() => {
                        setIsCharge(true);
                      }}
                      sx={{
                        "&:hover": {
                          backgroundColor: theme.colors.primary[2],
                        },
                      }}
                    >
                      <MdPlaylistAdd
                        size={30}
                        color={theme.colors.secondaryTextColor[6]}
                      />
                    </ActionIcon>
                  </Flex>
                </Flex>

                {scheduledCharges?.scheduledChargesByTransactionId.map(
                  (charge, index) => {
                    return (
                      <Flex
                        justify={"space-between"}
                        px={5}
                        py={10}
                        style={{
                          alignItems: "center",
                          justifyContent: "space-between",
                          background:
                            index % 2 !== 0 ? theme.colors.light[1] : "",
                          borderTop:
                            index % 2 !== 0
                              ? `1px solid ${theme.colors.light[3]}`
                              : "",
                          borderBottom:
                            index % 2 !== 0
                              ? `1px solid ${theme.colors.light[3]}`
                              : "",
                        }}
                      >
                        <Text size={14}>{charge.name}</Text>
                        <Flex>
                          <Text size={14}>$ {charge.cost}</Text>
                          <ActionIcon
                            ml={4}
                            size="sm"
                            variant="transparent"
                            onClick={() => {}}
                            sx={{
                              "&:hover": {
                                backgroundColor: theme.colors.primary[2],
                              },
                            }}
                          >
                            <BsThreeDotsVertical
                              color={theme.colors.primaryTextColor[6]}
                              onClick={() =>
                                handleOpenListMenu({
                                  name: "",
                                  id: charge.id,
                                })
                              }
                            />
                          </ActionIcon>
                        </Flex>
                        <Transition
                          mounted={activeOption?.id === charge.id}
                          transition="slide-left"
                          duration={200}
                          timingFunction="ease-in-out"
                        >
                          {(styles) => (
                            <Flex
                              justify="space-between"
                              px={10}
                              bg={theme.colors[theme.colorScheme][0]}
                              style={{
                                ...styles,
                                position: "absolute",
                                width: "-webkit-fill-available",
                                marginRight: "20px",
                                borderRadius: "5px",
                              }}
                              ref={outsideClickRef}
                            >
                              <ActionIcon
                                variant="light"
                                size="md"
                                color="blue.4"
                                my={4}
                                w="45%"
                                mah={25}
                                loading={false}
                                onClick={() => {
                                  handleEditScheduleCharge(charge || undefined);
                                }}
                              >
                                <>
                                  <IconPencil size={21} />
                                  <Text mx={10}>Edit Charge</Text>
                                </>
                              </ActionIcon>
                              <ActionIcon
                                variant="light"
                                size="md"
                                color="red.4"
                                my={4}
                                w="45%"
                                mah={25}
                                loading={deleteLoading}
                                onClick={() => {
                                  handleDeleteScheduleCharge(charge.id);
                                }}
                              >
                                <>
                                  <BiBlock size={21} />
                                  <Text mx={10}>Delete Charge</Text>
                                </>
                              </ActionIcon>
                              <CloseButton
                                size="md"
                                w={"10%"}
                                c={theme.colors.light[5]}
                                onClick={() => setActiveOption(undefined)}
                                my={4}
                              />
                            </Flex>
                          )}
                        </Transition>
                      </Flex>
                    );
                  }
                )}
              </Flex>
            </>
          ) : null}

          {modalState === CRUDSTATES.Create && (
            <Group position="apart" mt="md" style={{ marginTop: 30 }}>
              <Button
                radius="md"
                variant="outline"
                onClick={closeModal}
                style={{
                  flex: 1,
                  color: theme.colors.secondaryTextColor[6],
                }}
                sx={{
                  backgroundColor: theme.colors.secondary[6],
                  "&:hover": {
                    background: theme.colors.secondary[5],
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                radius="md"
                style={{
                  flex: 1,
                }}
                sx={{
                  color: theme.colors.primaryTextColor[6],
                  "&:hover": {
                    backgroundColor: theme.colors.primary[4],
                  },
                }}
                onClick={handleCreateSchedulePayment}
              >
                Create Charge
              </Button>
            </Group>
          )}
        </>
      ) : (
        <ScheduledCharge
          state={modalState}
          scheduleTransactionId={scheduleTransactionId!}
          subscriptionId={subscriptionId}
          charge={selectedCharge}
          setIsCharge={setIsCharge}
          onRefetchCharges={onRefetchCharges}
          closeModal={closeModal}
        />
      )}
    </>
  );
};

export default SchedulePayment;
